import React, { useState, useRef, useEffect } from 'react';
import Formsy from 'formsy-react';
import {
  RemoteChipSelectFormsy,
  SelectFormsy,
  TextFieldFormsy,
  ChipSelectFormsy,
} from 'components/formsy';
import {
  CommodityCodeDto,
  ETransportationMode,
  LocValue,
  TransactionScoringRequestDto,
  TransportationScoringRequestDto,
  TransportedProduct,
} from 'models/dtoTypes';
import { SearchHsCode } from 'services/openapiClient';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import { get, isEmpty } from 'lodash';
import { ScoreNewTransaction, GetLocationValues } from 'services/transaction';
import Notice from 'kosmos/components/Notice';
import { useHistory } from 'react-router';
import { Breadcrumbs, Grid, Paper, Stack, Typography } from '@mui/material';
import { countryOptions } from 'utils/countries';
import Link from 'components/Link';
import { transformOptionData } from 'utils/form';
import CompanyPicker from './_companyPicker';
import { useTranslation } from 'react-i18next';

const PREFIX = 'NewTransactionPage';

const classes = {
  root: `${PREFIX}-root`,
  pageHeader: `${PREFIX}-pageHeader`,
  input: `${PREFIX}-input`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  paddingBottom: theme.spacing(10),
  width: '100%',

  [`& .${classes.pageHeader}`]: {
    width: '100%',
    position: 'relative',

    '& > button': {
      position: 'absolute',
      right: 0,
      top: 0,
    },
  },
  [`& .${classes.input}`]: {
    width: '100%',
  },
}));

export interface IField {
  required?: boolean;
  label: string;
  widget: string;
  name: string;
  description?: string;
  options?: { alias: string; label: string }[];
  placeholder?: string;
}

export interface ISchema {
  fields: IField[];
}

interface INoticeData {
  message: string;
  severity: 'error' | 'success';
}

function NewTransactionPage(): JSX.Element {
  const history = useHistory();
  const [noticeData, setNoticeData] = React.useState<INoticeData>();
  const formRef = useRef<Formsy>(null);
  const [loading, setLoading] = useState(false);
  const [loadingFromOptions, setLoadingFromOptions] = useState(false);
  const [loadingToOptions, setLoadingToOptions] = useState(false);

  const { t } = useTranslation('default');
  const validatePostBody = (formData: Record<string, unknown>): boolean => {
    const fields = [
      'financialProduct',
      'financialProductReference',
      'transportationMode',
      'originLocode',
      'originLocation',
      'destinationLocode',
      'destinationLocation',
      'hsCode',
      'weightInKg',
      'countryOfOrigin',
      'buyerId',
      'sellerId',
    ];

    const invalidFields: string[] = [];

    fields.forEach((field) => {
      if (isEmpty(formData[field])) {
        invalidFields.push(field);
      }
    });

    if (invalidFields?.length) {
      return false;
    }
    return true;
  };

  const handleError = (e: unknown) => {
    if (axios.isAxiosError(e) && e.response) {
      const errorMessage = get(e.response?.data, 'message') || t('something-went-wrong');
      setNoticeData({
        message: errorMessage,
        severity: 'error',
      });
    } else {
      console.error(e);
      setNoticeData({
        message: e instanceof Error ? e.message : String(e),
        severity: 'error',
      });
    }
  };

  const handleCloseNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNoticeData(undefined);
  };

  const generatePostData = (transformed: Record<string, unknown>): TransactionScoringRequestDto => {
    const {
      hsCode,
      weightInKg,
      countryOfOrigin,
      transportationMode,
      originLocation,
      destinationLocation,
      financialProduct,
      financialProductReference,
      buyerId,
      sellerId,
    } = transformed;
    const formattedHSCode = ((hsCode as string) || '').split(':')[1];
    const productObj: TransportedProduct = {
      hsCode: formattedHSCode,
      weightInKg: weightInKg as number,
      countryOfOrigin: countryOfOrigin as string,
    };
    const transportObj: TransportationScoringRequestDto = {
      transportationMode: transportationMode as ETransportationMode,
      originLocation: originLocation as string,
      destinationLocation: destinationLocation as string,
      products: [productObj],
    };
    const requestBody: TransactionScoringRequestDto = {
      financialProduct: financialProduct as string,
      financialProductReference: financialProductReference as string,
      buyerId: buyerId as string,
      sellerId: sellerId as string,
      transportations: [transportObj],
    };
    return requestBody;
  };

  const [transportationMode, setTransportationMode] = useState<ETransportationMode>();
  const [selectedOrigin, setSelectedOrigin] = useState<{
    alias: string;
    label: string;
  }>({ alias: '', label: '' });
  const [selectedDestination, setSelectedDestination] = useState<{
    alias: string;
    label: string;
  }>({ alias: '', label: '' });
  const [selectedOriginLocation, setSelectedOriginLocation] = useState<string>('');
  const [selectedDestinationLocation, setSelectedDestinationLocation] = useState<string>('');
  const [originLocations, setOriginLocations] = useState<{ alias: string; label: string }[]>([]);
  const [destinationLocations, setDestinationLocations] = useState<
    { alias: string; label: string }[]
  >([]);

  useEffect(() => {
    handleOriginChange();
  }, [transportationMode, selectedOrigin]);

  useEffect(() => {
    handleDestinationChange();
  }, [transportationMode, selectedDestination]);

  const formatLocationOption = (item: LocValue): { alias: string; label: string } => {
    const label = item.locationName
      ? `${item.locationKey || ''} (${item.locationName || ''})`
      : item.locationKey;
    return {
      alias: item.locationKey || '',
      label: label || '',
    };
  };

  const handleOriginChange = async () => {
    if (transportationMode === ETransportationMode.Air && selectedOrigin.alias) {
      setSelectedOriginLocation('');
      setLoadingFromOptions(true);
      const modeResponse = await GetLocationValues(transportationMode, selectedOrigin.alias);
      setLoadingFromOptions(false);
      const locationOptions = modeResponse.locValues
        ? modeResponse.locValues.map(formatLocationOption)
        : [];
      setOriginLocations(locationOptions);
    } else {
      setSelectedOriginLocation('');
      setOriginLocations([]);
    }
  };

  const handleDestinationChange = async () => {
    // setSelectedDestination({ alias: '', label: '' });
    if (transportationMode === ETransportationMode.Air && selectedDestination.alias) {
      setSelectedDestinationLocation('');
      setLoadingToOptions(true);
      const modeResponse = await GetLocationValues(transportationMode, selectedDestination.alias);
      setLoadingToOptions(false);

      const locationOptions = modeResponse.locValues
        ? modeResponse.locValues.map(formatLocationOption)
        : [];
      setDestinationLocations(locationOptions);
    } else {
      // cleanup
      setSelectedDestinationLocation('');

      setDestinationLocations([]);
    }
  };

  const handleCreate = async (model: FormData) => {
    const transformed = transformOptionData(model as Record<string, unknown>);
    const flag = validatePostBody(transformed);
    const body = generatePostData(transformed);
    console.log('formData', transformed);
    if (!flag) {
      return setNoticeData({
        message: t('please-fill-in-all-the-fields-to-submit-for-scoring'),
        severity: 'error',
      });
    }
    setLoading(true);
    try {
      await ScoreNewTransaction(body);
      handleClose();
    } catch (e) {
      handleError(e);
    }
    setLoading(false);
  };

  const handleClose = () => {
    history.push('/transactions');
  };

  const handleSearchOriginLocodes = async ({ query }: { query: string }) => {
    if (transportationMode && query && query.length >= 3) {
      const modeResponse = await GetLocationValues(transportationMode, selectedOrigin.alias, query);
      const locationOptions = modeResponse.locValues
        ? modeResponse.locValues.map(formatLocationOption)
        : [];
      return { data: locationOptions };
    }
    return { data: [] };
  };

  const handleSearchDestLocodes = async ({ query }: { query: string }) => {
    if (transportationMode && query && query.length >= 3) {
      const modeResponse = await GetLocationValues(
        transportationMode,
        selectedDestination.alias,
        query,
      );
      const locationOptions = modeResponse.locValues
        ? modeResponse.locValues.map(formatLocationOption)
        : [];
      return { data: locationOptions };
    }
    return { data: [] };
  };

  const renderOriginalLocationCodeSelect = () => {
    if (!selectedOrigin.alias) {
      return null;
    } else if (transportationMode === ETransportationMode.Air) {
      return (
        <Paper elevation={0}>
          <ChipSelectFormsy
            name="originLocation"
            className={classes.input}
            required={true}
            textFieldProps={{
              label: t('airport-iata'),
              disabled: loadingFromOptions || loading,
              InputLabelProps: {
                shrink: true,
              },
              variant: 'outlined',
            }}
            disabled={loadingFromOptions || loading}
            placeholder={t('select-location')}
            label={t('airport-iata')}
            variant="outlined"
            options={originLocations}
            isMulti={false}
            value={selectedOriginLocation}
            onChange={(value: string) => setSelectedOriginLocation(value)}
          />
        </Paper>
      );
    } else {
      return (
        <Paper elevation={0}>
          <RemoteChipSelectFormsy
            value={selectedOrigin.alias}
            className={classes.input}
            name="originLocation"
            disabled={loadingFromOptions || loading}
            required={true}
            label={t('locode')}
            placeholder={t('type-to-search')}
            onSearchOptions={handleSearchOriginLocodes}
            optionRenderer={(item: { alias: string; label: string }) => item}
            variant="outlined"
            isMulti={false}
            onChange={(value: string) => setSelectedOriginLocation(value)}
          />
        </Paper>
      );
    }
  };

  const rendeDestLocationCodeSelect = () => {
    if (!selectedDestination.alias) {
      return null;
    } else if (transportationMode === ETransportationMode.Air) {
      return (
        <Paper elevation={0}>
          <ChipSelectFormsy
            name="destinationLocation"
            className={classes.input}
            required={true}
            textFieldProps={{
              label: t('airport-iata'),
              disabled: loadingToOptions || loading,
              InputLabelProps: {
                shrink: true,
              },
              variant: 'outlined',
            }}
            placeholder={t('select-location')}
            variant="outlined"
            options={destinationLocations}
            isMulti={false}
            value={selectedDestinationLocation}
            onChange={(value: string) => setSelectedDestinationLocation(value)}
          />
        </Paper>
      );
    } else {
      return (
        <Paper elevation={0}>
          <RemoteChipSelectFormsy
            className={classes.input}
            name="destinationLocation"
            disabled={loadingToOptions || loading}
            required={true}
            onSearchOptions={handleSearchDestLocodes}
            variant="outlined"
            isMulti={false}
            value={selectedDestination.alias}
            optionRenderer={(item: { alias: string; label: string }) => item}
            onChange={(value: string) => setSelectedDestinationLocation(value)}
          />
        </Paper>
      );
    }
  };

  const renderCardContent = () => (
    <Formsy
      ref={formRef}
      disabled={true}
      onSubmit={(model: FormData) => handleCreate(model)}
      // onValid={enableButton}
      // onChange={handleChange}
      // onInvalid={disableButton}
      className="form-container flex flex-col justify-center"
    >
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Typography component="div">
            <Box sx={{ fontWeight: 'bold', m: 1 }}>{t('financial-product')}</Box>
          </Typography>
          <Paper elevation={0}>
            <SelectFormsy
              className={classes.input}
              required={true}
              disabled={loading}
              name="financialProduct"
              label=""
              placeholder={t('financial-product')}
              variant="outlined"
              options={[
                { alias: 'Trade Finance', label: t('trade-finance') },
                {
                  alias: 'Supply Chain Finance',
                  label: t('supply-chain-finance'),
                },
                { alias: 'Trade Loan', label: t('trade-loan') },
                { alias: 'Receivables Finance', label: t('receivables-finance') },
              ]}
            />
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Typography component="div">
            <Box sx={{ fontWeight: 'bold', m: 1 }}>{t('financial-product-reference')}</Box>
          </Typography>
          <Paper elevation={0}>
            <TextFieldFormsy
              className={classes.input}
              disabled={loading}
              required={true}
              type="text"
              name="financialProductReference"
              label=""
            />
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Typography component="div">
            <Box sx={{ fontWeight: 'bold', m: 1 }}>{t('buyer-information')}</Box>
          </Typography>
          <Paper style={{ padding: 20 }} elevation={2}>
            <CompanyPicker name={'buyerId'} />
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Typography component="div">
            <Box sx={{ fontWeight: 'bold', m: 1 }}>{t('seller-information')}</Box>
          </Typography>
          <Paper style={{ padding: 20 }} elevation={2}>
            <CompanyPicker name={'sellerId'} />
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Typography component="div">
            <Box sx={{ fontWeight: 'bold', m: 1 }}>{t('product-information')}</Box>
          </Typography>
          <Paper style={{ padding: 20 }} elevation={2}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography component="div">
                  <Box sx={{ m: 1 }}>{t('product-hs-code-description')}</Box>
                </Typography>
                <Paper elevation={0}>
                  <RemoteChipSelectFormsy
                    className={classes.input}
                    name="hsCode"
                    disabled={loading}
                    required={true}
                    onSearchOptions={(searchText: { query: string }) =>
                      SearchHsCode({
                        query: searchText.query,
                        hsDigitLength: 6,
                        ebopsCodesSuppressed: true,
                      })
                    }
                    optionRenderer={({ id, commodity }: CommodityCodeDto) => ({
                      alias: id,
                      label: ` (${(id || '').toUpperCase()}) ${commodity as string}`,
                    })}
                    variant="outlined"
                    isMulti={false}
                  />
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Typography component="div">
                  <Box sx={{ m: 1 }}>{t('weight-in-kg')}</Box>
                </Typography>
                <Paper elevation={0}>
                  <TextFieldFormsy
                    className={classes.input}
                    disabled={loading}
                    required={true}
                    type="number"
                    name="weightInKg"
                    label=""
                  />
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Typography component="div">
                  <Box sx={{ m: 1 }}>{t('country-of-origin')}</Box>
                </Typography>
                <Paper elevation={0}>
                  <ChipSelectFormsy
                    name="countryOfOrigin"
                    className={classes.input}
                    required={true}
                    textFieldProps={{
                      variant: 'outlined',
                    }}
                    variant="outlined"
                    options={countryOptions()}
                    isMulti={false}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Typography component="div">
            <Box sx={{ fontWeight: 'bold', m: 1 }}>{t('transportation-information')}</Box>
          </Typography>
          <Paper style={{ padding: 20 }} elevation={2}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography component="div">
                  <Box sx={{ m: 1 }}>{t('mode-of-transport')}</Box>
                </Typography>
                <Paper elevation={0}>
                  <SelectFormsy
                    className={classes.input}
                    required={true}
                    disabled={loading}
                    name="transportationMode"
                    label=""
                    placeholder="transportationMode"
                    variant="outlined"
                    options={[
                      {
                        alias: ETransportationMode.Maritime,
                        label: 'Maritime',
                      },
                      { alias: ETransportationMode.Rail, label: 'Rail' },
                      { alias: ETransportationMode.Road, label: 'Road' },
                      { alias: ETransportationMode.Air, label: 'Air' },
                    ]}
                    onChange={(
                      event: React.ChangeEvent<{
                        value: ETransportationMode;
                      }>,
                    ) => setTransportationMode(event.target.value)}
                  />
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Typography component="div">
                  <Box sx={{ m: 1 }}>{t('origin')}</Box>
                </Typography>
                <Paper elevation={0}>
                  <ChipSelectFormsy
                    name="originLocode"
                    className={classes.input}
                    required={true}
                    textFieldProps={{
                      variant: 'outlined',
                    }}
                    label={t('select-country')}
                    variant="outlined"
                    options={countryOptions()}
                    isMulti={false}
                    value={selectedOrigin}
                    onChange={(value: { alias: string; label: string }) => setSelectedOrigin(value)}
                  />
                </Paper>
                <div>
                  <Typography component="div">&nbsp;</Typography>
                  {renderOriginalLocationCodeSelect()}
                </div>
              </Grid>
              <Grid item xs={4}>
                <Typography component="div">
                  <Box sx={{ m: 1 }}>{t('destination')}</Box>
                </Typography>
                <Paper elevation={0}>
                  <ChipSelectFormsy
                    name="destinationLocode"
                    className={classes.input}
                    required={true}
                    textFieldProps={{
                      variant: 'outlined',
                    }}
                    label={t('select-country')}
                    variant="outlined"
                    options={countryOptions()}
                    isMulti={false}
                    value={selectedDestination}
                    onChange={(value: { alias: string; label: string }) =>
                      setSelectedDestination(value)
                    }
                  />
                </Paper>
                {selectedDestination.alias !== '' ? (
                  <div>
                    <Typography component="div">&nbsp;</Typography>
                    <Paper elevation={0}>{rendeDestLocationCodeSelect()}</Paper>
                  </div>
                ) : null}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper style={{ padding: 0 }} elevation={0}>
            <Stack direction="row" spacing={2}>
              <Button disabled={loading} variant="outlined" onClick={handleClose} color="primary">
                {t('cancel')}
              </Button>
              <Button
                disabled={loading}
                type="submit"
                variant="contained"
                color="primary"
                autoFocus
              >
                {t('submit-for-scoring')}
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Formsy>
  );

  return (
    <Root>
      <Breadcrumbs aria-label="breadcrumb">
        <Link to="/" text={t('home')} />
        <Link to={'/transactions'} text={t('transactions')} />
        <Typography color="text.primary">{t('new')}</Typography>
      </Breadcrumbs>
      <div className={classes.pageHeader}>
        <h2>{t('score-new-transaction')}</h2>
      </div>
      {renderCardContent()}
      {loading && <LinearProgress />}
      {noticeData && (
        <Notice
          open={true}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseNotice}
        />
      )}
    </Root>
  );
}

export default NewTransactionPage;
